import { isBackgroundStyle, isColorStyle } from '../../../../../components/editor/editorUtils';
import { convertMarkToStyle } from '../../../../../../common/tiptap/conversion/editorConversion/toDraftJs/convertTiptapMarksToInlineStyleRanges';
import { convertInlineStyleToTiptapMark } from '../../../../../../common/tiptap/conversion/editorConversion/toTiptap/convertDraftInlineStyleToTiptapMark';
import { TiptapNodeType } from '../../../../../../common/tiptap/tiptapTypes';
import type { TiptapContent, TiptapContentMark, TiptapContentNode } from '../../../../../../common/tiptap/tiptapTypes';

/**
 * Returns whether the mark should be cleared before applying the next whole-cell text-style state.
 */
const shouldRemoveMarkForWholeCellTextStyleUpdate = ({
    mark,
    textStyle,
    shouldAddInlineStyle,
    isColorOrBackgroundTextStyleChange,
}: {
    mark: TiptapContentMark;
    textStyle: string | null;
    shouldAddInlineStyle: boolean;
    isColorOrBackgroundTextStyleChange: boolean;
}): boolean => {
    const markStyle = convertMarkToStyle(mark);
    if (!markStyle) return false;

    if (shouldAddInlineStyle && isColorOrBackgroundTextStyleChange) {
        return isColorStyle(markStyle) || isBackgroundStyle(markStyle);
    }

    return markStyle === textStyle;
};

/**
 * Returns the next mark list for a text node after a whole-cell style toggle.
 */
const getUpdatedTextNodeMarks = ({
    marks,
    textStyle,
    shouldAddInlineStyle,
    isColorOrBackgroundTextStyleChange,
}: {
    marks?: TiptapContentMark[];
    textStyle: string | null;
    shouldAddInlineStyle: boolean;
    isColorOrBackgroundTextStyleChange: boolean;
}): TiptapContentMark[] | undefined => {
    const filteredMarks = (marks || []).filter(
        (mark) =>
            !shouldRemoveMarkForWholeCellTextStyleUpdate({
                mark,
                textStyle,
                shouldAddInlineStyle,
                isColorOrBackgroundTextStyleChange,
            }),
    );

    if (!shouldAddInlineStyle || !textStyle) return filteredMarks.length ? filteredMarks : undefined;

    const nextMark = convertInlineStyleToTiptapMark(textStyle);
    if (!nextMark) return filteredMarks.length ? filteredMarks : undefined;

    return [...filteredMarks, nextMark];
};

/**
 * Recursively applies the updated whole-cell marks to every text node in the Tiptap document.
 */
const updateNodeWholeCellTextStyle = ({
    node,
    textStyle,
    shouldAddInlineStyle,
    isColorOrBackgroundTextStyleChange,
}: {
    node: TiptapContentNode;
    textStyle: string | null;
    shouldAddInlineStyle: boolean;
    isColorOrBackgroundTextStyleChange: boolean;
}): TiptapContentNode => {
    if (node.type === TiptapNodeType.text) {
        return {
            ...node,
            marks: getUpdatedTextNodeMarks({
                marks: node.marks,
                textStyle,
                shouldAddInlineStyle,
                isColorOrBackgroundTextStyleChange,
            }),
        };
    }

    if (!node.content) return node;

    return {
        ...node,
        content: node.content.map((child) =>
            updateNodeWholeCellTextStyle({
                node: child,
                textStyle,
                shouldAddInlineStyle,
                isColorOrBackgroundTextStyleChange,
            }),
        ),
    };
};

/**
 * Rewrites persisted Tiptap marks so whole-cell toolbar styles stay in sync with rich cell content.
 */
export const updateTiptapContentForWholeCellTextStyle = ({
    content,
    textStyle,
    shouldAddInlineStyle,
    isColorOrBackgroundTextStyleChange,
}: {
    content: TiptapContent;
    textStyle: string | null;
    shouldAddInlineStyle: boolean;
    isColorOrBackgroundTextStyleChange: boolean;
}): TiptapContent => ({
    ...content,
    content: content.content.map((node) =>
        updateNodeWholeCellTextStyle({
            node,
            textStyle,
            shouldAddInlineStyle,
            isColorOrBackgroundTextStyleChange,
        }),
    ),
});
